import { createApp } from 'vue';
import hoverPhoto from '@/directives/hoverPhoto';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap';
import '@fortawesome/fontawesome-free/css/all.css';
import '@fortawesome/fontawesome-free/css/fontawesome.css';
import 'animate.css';
import './style.css';

import App from './App.vue';
import router from './router';
import { createPinia } from 'pinia';
import { createHead } from '@vueuse/head';
import { useAuthStore } from '@/stores/auth';
import { initAnalytics, trackPageview } from '@/analytics/track';
import { initPixels } from '@/analytics/pixels';

// Create the Vue app instance
const app = createApp(App);
app.directive('hover-photo', hoverPhoto);   // menu cards: Default photo → Hover photo under the mouse
import { installErrorBeacon } from '@/utils/errorBeacon';
installErrorBeacon(app);
// Switching sign-up -> sign-in (or into the code step) closes one modal as the next opens; the
// closing one removed the page scroll-lock the new one had just set. Keep it while any is open.
document.addEventListener('hidden.bs.modal', () => {
  if (document.querySelector('.modal.show')) document.body.classList.add('modal-open');
});

// Create and use the Pinia store
const pinia = createPinia();

// Persist the shopping cart across page reloads / reopening the tab. Without this, the
// cart lives only in memory and a refresh (or a return trip from the payment page) wipes
// it, forcing the customer to shop again. We save only the durable bits (line items +
// applied coupon); totals are recomputed on hydrate. Scoped to the cart store.
pinia.use(({ store }) => {
  if (store.$id !== 'cart') return;
  const KEY = 'chds_cart_v1';
  try {
    const saved = JSON.parse(localStorage.getItem(KEY) || 'null');
    if (saved && Array.isArray(saved.cart) && saved.cart.length) {
      store.cart = saved.cart;
      store.appliedCoupon = saved.appliedCoupon || null;
      if (typeof store.updateTotal === 'function') store.updateTotal();
    }
  } catch (e) { /* corrupt/full storage must never break the app */ }
  store.$subscribe((_mutation, state) => {
    try {
      localStorage.setItem(KEY, JSON.stringify({ cart: state.cart, appliedCoupon: state.appliedCoupon }));
    } catch (e) { /* ignore quota / privacy-mode errors */ }
  });
});

app.use(pinia);

// Create and use VueUse Head for SEO
const head = createHead();
app.use(head);

// Use the router
app.use(router);

// CHDS Circle: remember a ?ref code for 30 days (last click wins; a typed code beats it).
import { useCircleStore } from '@/stores/circle';
try { useCircleStore().captureFromUrl(); } catch (e) { /* never block boot */ }

router.afterEach((to) => {
    // Update the document title
    if (to.meta.title) {
      document.title = to.meta.title;
    } else {
      document.title = 'Chi Hun Da Su';
    }
    // First-party analytics: record a page view for storefront routes only.
    if (!(to.path || '').startsWith('/dashboard')) {
      // defer so document.title is updated before we send it
      setTimeout(() => trackPageview(), 0);
    }
});

// Mount the app to the DOM
app.mount('#app');
// Static SEO shells (render_seo_pages) ship their own head tags for crawlers; the live app
// manages the same tags, so drop the static copies once we're running.
try { document.querySelectorAll('[data-seo-static]').forEach((el) => el.remove()); } catch (e) { /* noop */ }

// Initialize authentication after the app is mounted
const authStore = useAuthStore();
authStore.initializeAuth();

// First-party analytics + marketing pixels (storefront only).
if (!(window.location.pathname || '').startsWith('/dashboard')) {
  initAnalytics();
  initPixels();
}
